import {Component, computed, effect, inject, Input, OnChanges, signal, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {BaseChartComponent, type EnergyType, type Interval} from './base-chart.component';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {AverageEnergyStatus} from '../../../../api/models/average-energy-status';
import {BaseChartDirective} from 'ng2-charts';
import {de} from 'date-fns/locale';
import {Chart, ChartDataset, ChartOptions} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import {rxResource} from '@angular/core/rxjs-interop';
import {DateTimeValue} from '../../../../api/models/date-time-value';
import {SunResponse} from '../../../../api/models/sun-response';
import {LocationStateService} from '../../../../common/services/location-state.service';
import {environment} from '../../../../environments/environment';
import {getAverageEnergyStatus} from './average-energy-response.helper';

@Component({
  selector: 'app-usage-chart',
  templateUrl: './chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective],
})
export class UsageChartComponent extends BaseChartComponent implements OnChanges {
  @Input() override interval: Interval = 'day';
  protected override get summaryType(): EnergyType { return 'production'; }
  private _interval = signal<Interval>('day');

  @Input() set date(value: Date) {
    this._date.set(value);
  }

  get date(): Date {
    return this._date();
  }

  private _date = signal<Date>(new Date());
  @Input() data: AverageEnergyResponse | undefined;

  private readonly locationStateService = inject(LocationStateService);

  // Weather resource for sunrise/sunset data - automatically reloads when date changes
  weatherResource = rxResource<SunResponse, any>({
    params: computed(() => ({body: {date: this._date().toISOString()}})),
    defaultValue: environment.showDefault
      ? {
          sunrise: this._date().toISOString(),
          sunset: this._date().toISOString(),
        }
      : {sunrise: undefined, sunset: undefined},
    stream: this.sunDataLoader,
  });

  lineChartOption: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom', // Position the legend at the top
        display: true,
        labels: {
          generateLabels: (chart) => {
            const original = Chart.defaults.plugins.legend.labels.generateLabels(chart);
            return original.map((label) => {
              const dataset = chart.data.datasets[label.datasetIndex ?? 0] as any;
              if (this.isCapLineDataset(dataset)) {
                return this.styleLegendItemAsCap(
                  label,
                  this.getDatasetColorValue(dataset?.borderColor),
                  Number(dataset?.borderWidth ?? this.getCapLineBorderWidth()),
                );
              }
              if (label.text === 'Produktion') {
                const root = document.documentElement;
                const productionColor = getComputedStyle(root).getPropertyValue('--color-production-yellow').trim();
                return {
                  ...label,
                  strokeStyle: productionColor,
                  fillStyle: productionColor,
                  color: productionColor,
                };
              }
              return label;
            });
          },
        },
      },
      annotation: {
        annotations: {},
      },
    },
    scales: {
      y: {
        stacked: true,
        ticks: {
          stepSize: 1, // Display every 0.50 on the y-axis
          precision: 0, // Ensures ticks are displayed with two decimal places
        },
        beginAtZero: true,
        // Initial values; will be updated from LocationStateService.currentLocation
        min: 0,
        max: 0,
        border: {
          display: false
        }
      },
      x: {
        stacked: true,
        type: 'time',
        time: {
          unit: 'hour',
          tooltipFormat: 'HH:mm',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        border: {
          display: false
        }
      },
    },
  };

  private readonly sunriseSunsetEffect = effect(() => {
    const sun = this.weatherResource.value();
    const currentInterval = this._interval();

    if (currentInterval !== 'day') {
      this.removeAnnotations();
      return;
    }

    this.removeAnnotations();

    let updatedOptions = {...this.lineChartOption};

    if (sun?.sunrise) {
      updatedOptions = this.addVerticalLine(updatedOptions, new Date(sun.sunrise), 'Sonnenaufgang', 'rgb(128,128,128)');
    }
    if (sun?.sunset) {
      updatedOptions = this.addVerticalLine(updatedOptions, new Date(sun.sunset), 'Sonnenuntergang', 'rgb(128,128,128)');
    }

    // Subtle marker for the current time (only shown when viewing today)
    updatedOptions = this.addCurrentTimeLine(updatedOptions, this._date());

    this.lineChartOption = updatedOptions;
    this.chartOptions.set(this.lineChartOption);

    if (this.chart) {
      this.chart.update();
    }
  });

  constructor() {
    super();
    Chart.register(annotationPlugin);

    // React to location changes and update chart bounds
    effect(() => {
      const range = this.locationStateService.currentLocation()?.displayChart?.performanceOrigin;
      if (!range) {
        return;
      }

      this.lineChartOption = {
        ...this.lineChartOption,
        scales: {
          ...this.lineChartOption.scales,
          y: {
            ...(this.lineChartOption.scales?.['y'] ?? {}),
            beginAtZero: true,
            min: range.minkWh ?? undefined,
            max: range.maxkWh ?? undefined,
          },
          x: this.lineChartOption.scales?.['x'],
        },
      };

      this.chartOptions.set(this.lineChartOption);
      if (this.chart) {
        this.chart.update();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Usage chart received change', changes);
    if (changes['interval']) {
      this._interval.set(this.interval);
    }
    if (changes['data'] || changes['date'] || changes['interval']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    this.intializeChartData();
  }

  private intializeChartData() {
    console.log('initialize Usage Chart');
    if (!this.data) {
      return; // Exit if no data is available
    }

    const productionStatus = this.getStatus(getAverageEnergyStatus(this.data, 'production'));
    const completedProductionValues = this.processStatusValues(productionStatus);
    const consumptionStatus = this.getStatus(getAverageEnergyStatus(this.data, 'consumption'));
    const gridStatus = this.getStatus(getAverageEnergyStatus(this.data, 'gridNegative') ?? getAverageEnergyStatus(this.data, 'grid'));
    const batteryStatus = this.getStatus(getAverageEnergyStatus(this.data, 'batteryPowerPositive') ?? getAverageEnergyStatus(this.data, 'batteryPower'));

    const completedConsumptionValues = this.processStatusValues(consumptionStatus);
    const completedGridValues = this.processStatusValues(gridStatus);
    const completedBatteryValues = this.processStatusValues(batteryStatus);

    const cappedUsage = this.capStackToProduction(
      completedConsumptionValues,
      completedGridValues,
      completedBatteryValues,
      completedProductionValues,
    );

    const consumptionDataset = this.createStackedDataset(this.getLabel('consumption'), cappedUsage.consumption, 'consumption');
    const gridDataset = this.createStackedDataset(this.getLabel('grid'), cappedUsage.grid, 'grid');
    const batteryDataset = this.createStackedDataset(this.getLabel('battery'), cappedUsage.battery, 'battery');
    const productionDataSet = this.createCapDataset(this.getLabel('production'), completedProductionValues, 'production');

    // Add labels only once (based on production values)
    const labels: Date[] = this.populateLabels(completedProductionValues);

    const datasets = [];
    // Positive-only stack capped by production.
    datasets.push(consumptionDataset);
    datasets.push(gridDataset);
    datasets.push(batteryDataset);
    datasets.push(productionDataSet);

    // Update chart data
    this.updateChart(labels, datasets);
  }

  /**
   * Extracts the status object or provides a default.
   */
  private getStatus(status: AverageEnergyStatus | undefined): AverageEnergyStatus {
    return status ?? {deviceNames: {}, deviceTimeValues: {}, timeBucket: ''};
  }

  /**
   * Processes device time values by summarizing and completing them.
   */
  private processStatusValues(status: AverageEnergyStatus): DateTimeValue[] {
    const summarizedValues = super.summarizeByTimestamp(Object.values(status.deviceTimeValues || {}));
    return super.completeData(summarizedValues, 'day', this.date, 'zero', status.timeBucket);
  }

  /**
   * Caps consumption + grid export + battery charge per point so the stacked sum never exceeds production.
   *
   * Allocation is priority-based per timestamp:
   *  1. Use production for consumption first.
   *  2. Then allocate remaining production to grid export.
   *  3. Finally allocate remaining production to battery charging.
   */
  private capStackToProduction(
    consumption: DateTimeValue[],
    grid: DateTimeValue[],
    battery: DateTimeValue[],
    production: DateTimeValue[],
  ): { consumption: DateTimeValue[]; grid: DateTimeValue[]; battery: DateTimeValue[] } {
    const c = consumption.map((v) => ({...v, value: Math.max(0, v.value ?? 0)}));
    const g = grid.map((v) => ({...v, value: Math.max(0, -(v.value ?? 0))}));
    const b = battery.map((v) => ({...v, value: Math.max(0, v.value ?? 0)}));
    const p = production.map((v) => ({...v, value: Math.max(0, v.value ?? 0)}));

    const idxByRead = (arr: DateTimeValue[]) => {
      const m = new Map<string, number>();
      arr.forEach((v, i) => {
        if (v.read) m.set(v.read, i);
      });
      return m;
    };

    const cIdx = idxByRead(c);
    const gIdx = idxByRead(g);
    const bIdx = idxByRead(b);

    p.forEach((pv) => {
      if (!pv.read) return;
      let remaining = Math.max(0, pv.value ?? 0);

      const ci = cIdx.get(pv.read);
      const gi = gIdx.get(pv.read);
      const bi = bIdx.get(pv.read);

      const cv = ci !== undefined ? (c[ci].value ?? 0) : 0;
      const gv = gi !== undefined ? (g[gi].value ?? 0) : 0;
      const bv = bi !== undefined ? (b[bi].value ?? 0) : 0;

      if (remaining <= 0 || (cv <= 0 && gv <= 0 && bv <= 0)) {
        if (ci !== undefined) c[ci].value = 0;
        if (gi !== undefined) g[gi].value = 0;
        if (bi !== undefined) b[bi].value = 0;
        return;
      }

      const useConsumption = Math.min(cv, remaining);
      remaining -= useConsumption;

      const useGrid = Math.min(gv, remaining);
      remaining -= useGrid;

      const useBattery = Math.min(bv, remaining);

      if (ci !== undefined) c[ci].value = useConsumption;
      if (gi !== undefined) g[gi].value = useGrid;
      if (bi !== undefined) b[bi].value = useBattery;
    });

    return {consumption: c, grid: g, battery: b};
  }



  /**
   * Populates labels based on processed values.
   */
  private populateLabels(values: DateTimeValue[]): Date[] {
    const labels: Date[] = [];
    values.forEach((item) => {
      const date = new Date(item.read ?? '');
      if (!isNaN(date.getTime())) {
        labels.push(date);
      }
    });
    return labels;
  }

  /**
   * Creates a stacked dataset for the positive usage side.
   */
  private createStackedDataset(label: string, values: DateTimeValue[], colorKey: string): ChartDataset {
    const color = this.getColor(colorKey);
    return {
      label,
      data: values.map((v) => v.value!),
      borderColor: color,
      backgroundColor: color,
      fill: true,
      pointRadius: 0, // Hide dots for better readability
      pointHoverRadius: 0,
      tension: 0.1, // Smooth interpolation between points
      cubicInterpolationMode: 'default',
      stack: 'stack1',
      borderWidth: this.isMobile() ? 1 : 2,
    };
  }

  /**
   * Creates the production cap line without the flipped fill.
   */
  private createCapDataset(label: string, values: DateTimeValue[], colorKey: string): ChartDataset {
    const color = this.getColor(colorKey);
    const dataset = {
      label,
      data: values.map((v) => v.value!),
      borderColor: color,
      backgroundColor: 'transparent',
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 0,
      tension: 0.1,
      cubicInterpolationMode: 'default',
      borderWidth: this.getCapLineBorderWidth(),
      isCapLine: true,
      order: 99,
    } as ChartDataset & {isCapLine: boolean};
    return dataset;
  }

  /**
   * Updates the chart with the provided labels and datasets.
   */
  private updateChart(labels: Date[], datasets: Record<string, any>[]): void {
    this.chartType.set('line');
    // Re-apply the current-time marker on every rebuild so it survives view switches
    if (this._interval() === 'day') {
      this.lineChartOption = this.addCurrentTimeLine(this.lineChartOption, this._date());
    }
    this.chartOptions.set(this.lineChartOption);
    this.chartData.set({
      labels: labels,
      datasets: datasets,
    });
    if (this.chart) {
      this.chart.update();
    }
  }

  private removeAnnotations() {
    if (this.lineChartOption.plugins?.annotation) {
      this.lineChartOption.plugins.annotation.annotations = {};
      this.lineChartOption = {...this.lineChartOption};
    }

    if (this.chart?.options?.plugins?.annotation) {
      this.chart.options.plugins.annotation.annotations = {};
    }

    this.chartOptions.set(this.lineChartOption);

    if (this.chart) {
      this.chart.update();
    }
  }

  plugin = {
    id: 'cutterPlugin',
    afterDatasetsDraw(chart: any) {
      const {
        ctx,
        chartArea: {top, bottom},
        scales: {x, y},
      } = chart;
      const baseDataset = chart.data.datasets[0];
      const topDataset = chart.data.datasets[1];
      const cutDataset = chart.data.datasets[2];

      chart.data.labels.forEach((label: any) => {
        const i = chart.data.labels.indexOf(label);
        const base = baseDataset.data[i];
        const topVal = topDataset.data[i];
        const stackTotal = base + topVal;
        const cutVal = cutDataset.data[i];

        if (cutVal >= stackTotal) {
          // draw nothing — entire stack is under the cutter
          return;
        }

        if (cutVal <= 0) return; // nothing to cut

        const barWidth = x.getPixelForTick(i + 1) - x.getPixelForTick(i);
        const centerX = x.getPixelForValue(i);
        const barLeft = centerX - barWidth / 4;
        const barRight = centerX + barWidth / 4;

        const yCut = y.getPixelForValue(cutVal);
        const yTop = y.getPixelForValue(stackTotal);

        ctx.save();
        ctx.fillStyle = 'white';
        ctx.fillRect(barLeft, yTop, barRight - barLeft, yCut - yTop);
        ctx.restore();
      });
    },
  };
}
